/** English UI copy for Muslim Invest investor & company profile forms (translated via useTranslateDynamicFields). */

export const INVESTOR_FORM_UI_TEXT = {
  investorOverview: "Investor Overview",
  investmentType: "Investment Type",
  investmentTypeRequired: "Investment Type is required",
  sectorsOfInterest: "Sectors of Interest",
  sectorsRequired: "At least one sector must be selected",
  selectSectors: "Select sectors...",
  stageOfDevelopment: "Stage of Development",
  developmentStageRequired: "Development Stage is required",
  selectStage: "Select Stage",
  communicationPreferences: "Communication Preferences",
  frequencyOfUpdates: "Frequency of Updates",
  frequencyRequired: "Frequency of Updates is required",
  pleaseSelect: "Please select",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  presentationFormatPreferences: "Presentation Format Preferences",
  presentationRequired: "At least one presentation format must be selected",
  selectPresentationFormats: "Select presentation formats...",
  writtenReports: "Written Reports",
  videoPitches: "Video Pitches",
  inPersonPitches: "In-Person Pitches",
  investmentCriteria: "Investment Criteria",
  investmentTimeHorizon: "Investment Time Horizon",
  timeHorizonRequired: "At least one investment time horizon must be selected",
  selectTimeHorizon: "Select investment time horizon...",
  shortTerm: "Short Term",
  mediumTerm: "Medium Term",
  longTerm: "Long Term",
  numberOfPreviousInvestments: "Number of Previous Investments",
  previousInvestmentsRequired: "Number of Previous Investments is required",
  selectPreviousInvestments: "Select number of Previous Investments",
  investmentTicketSize: "Investment Ticket Size",
  ticketSizeRequired: "At least one investment ticket size must be selected",
  selectTicketSizes: "Select Investment Ticket Sizes...",
  experienceAndPreferences: "Experience and Preferences",
  experienceEisSeis: "Experience with EIS/SEIS",
  taxBreaksImportant: "Are Tax Breaks Important?",
  thirdPartyReview: "Do you require a Third Party Company Review?",
  thirdPartyPlatform: "Will you be using a Third Party Platform for Transactions?",
  fieldRequired: "This field is required",
  selectOption: "Select an option",
  yes: "Yes",
  no: "No",
  individualInvestor: "Individual Investor",
  investmentBusiness: "Investment Business",
  seed: "Seed",
  earlyStage: "Early Stage",
  growth: "Growth",
  expansion: "Expansion",
  back: "Back",
  saveContinue: "Save & Continue",
  prevInvest0to5: "0-5",
  prevInvest6to10: "6-10",
  prevInvest11to50: "11-50",
  prevInvest51to100: "51-100",
  prevInvest100plus: "100+",
  ticket1k10k: "£1k-£10k",
  ticket10k50k: "£10k-£50k",
  ticket50k100k: "£50k-£100k",
  ticket100k500k: "£100k-£500k",
  ticket500k1m: "£500k-£1M",
  ticket1m5m: "£1M-£5M",
  ticket5mPlus: "£5M+",
} as const;

export type InvestorFormUiKey = keyof typeof INVESTOR_FORM_UI_TEXT;

export const COMPANY_FORM_UI_TEXT = {
  companyProfileDetails: "Company Profile Details",
  companyOverview: "Company Overview",
  companyName: "Company Name",
  companyNameRequired: "Company Name is required",
  enterCompanyName: "Enter Company Name",
  companyDescription: "Company Description",
  companyDescriptionRequired: "Company Description is required",
  shortDescription: "Short Description",
  companyRegistrationNumber: "Company Registration Number",
  companyRegistrationNumberRequired: "Company Registration Number is required",
  enterCompanyRegistrationNumber: "Enter Company Registration Number",
  countryOfRegistration: "Country of Registration",
  countryOfRegistrationRequired: "Country of Registration is required",
  selectCountry: "Select Country",
  missionStatement: "Mission Statement",
  missionStatementRequired: "Mission Statement is required",
  vision: "Vision",
  visionRequired: "Vision is required",
  sectorsOfInterest: "Sectors of Interest",
  sectorsRequired: "At least one sector must be selected",
  selectSectors: "Select sectors...",
  stageOfDevelopment: "Stage of Development",
  developmentStageRequired: "Development Stage is required",
  selectStage: "Select Stage",
  seed: "Seed",
  earlyStage: "Early Stage",
  growth: "Growth",
  expansion: "Expansion",
  foundingTeam: "Founding Team",
  addMember: "Add Member",
  firstName: "First Name",
  firstNameRequired: "First Name is required",
  enterFirstName: "Enter First Name",
  lastName: "Last Name",
  lastNameRequired: "Last Name is required",
  enterLastName: "Enter Last Name",
  role: "Role",
  roleRequired: "Role is required",
  email: "Email",
  emailRequired: "Email is required",
  emailInvalid: "Please enter a valid email address",
  enterEmail: "Enter your email",
  linkedInWebsite: "LinkedIn Profile/Website",
  urlRequired: "URL is required",
  urlInvalid: "Please enter a valid URL",
  enterLinkedInUrl: "Enter Your LinkedIn Profile or Website Url",
  save: "Save",
  cancel: "Cancel",
  emailLabel: "Email:",
  linkedInLabel: "LinkedIn:",
  viewProfile: "View Profile",
  productsServices: "Products/Services",
  productServiceDescription: "Product/Service Description",
  productServiceDescriptionRequired: "Product/Service Description is required",
  enterProductServiceDescription: "Enter Product/Service Description",
  productMarketDifferentiation: "Product Market Differentiation",
  productMarketDifferentiationRequired: "Product Market Differentiation is required",
  enterProductMarketDifferentiation: "Enter Product Market Differentiation",
  targetMarket: "Target Market",
  targetMarketRequired: "Target Market is required",
  enterTargetMarket: "Enter Target Market",
  landscape: "Landscape",
  landscapeRequired: "Landscape is required",
  enterLandscape: "Enter Landscape",
  financialsGbp: "Financials (in GBP)",
  revenueProjections: "Revenue Projections",
  revenueProjectionsRequired: "Revenue Projections is required",
  enterRevenueProjections: "Enter Revenue Projections",
  profitMargins: "Profit Margins",
  profitMarginsRequired: "Profit Margins is required",
  enterProfitMargins: "Enter Profit Margins",
  expenses: "Expenses",
  expensesRequired: "Expenses are required",
  enterExpenses: "Enter Expenses",
  amountSought: "Amount Sought",
  amountSoughtRequired: "Amount Sought is required",
  enterAmountSought: "Enter Amount Sought",
  useOfFunds: "Use of Funds",
  useOfFundsRequired: "Use of Funds is required",
  enterUseOfFunds: "Enter Use of Funds",
  back: "Back",
  saveContinue: "Save & Continue",
} as const;

export type CompanyFormUiKey = keyof typeof COMPANY_FORM_UI_TEXT;

/** Read-only user summary step before investor/company detail forms. */
export const PROFILE_SHELL_UI_TEXT = {
  profile: "Profile",
  name: "Name",
  email: "Email",
  phone: "Phone",
  membershipType: "Membership Type",
  next: "Next",
} as const;

export type ProfileShellUiKey = keyof typeof PROFILE_SHELL_UI_TEXT;
